import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Editor from "../components/Editor";
import ToolbarItemPicker from "./components/ToolbarItemPicker";
import RecipientsSelect from "../components/RecipientsSelect";
import { useState, useRef } from "react";
import InputDebounce from "../components/InputDebounce";
import EmailSnippetPicker from "./components/EmailSnippetPicker";
import EmailVariablePicker from "./components/EmailVariablePicker";
import EmailCanvaPicker from "./components/EmailCanvaPicker";

const ComposeEmail = ({
  projectId,
  isEditMode,
  emailData,
  onDataChange,
  validationErrors,
  editorInputDescriptionRef,
  navigateToUrl,
}) => {
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const currentUserEmail = currentUser.get("email");
  const members = useSelector((state: any) => state.projectReducer.getIn(["project", "members"]));
  const attributes = useSelector((state: any) => state.template.get("attributes"));

  const [showCcBcc, setShowCcBcc] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const variablePickerOpenRef = useRef<(() => void) | null>(null);
  const canvaPickerOpenRef = useRef<(() => void) | null>(null);

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const handleSelectSearchItem = (searchItem) => {
    const item = {
      value: searchItem.get("id"),
      label: searchItem.getIn(["data", "name", "value"]),
      type: searchItem.get("type"),
    };
    handleChangeField("item", item);
    handleChangeField("itemId", searchItem.get("id"));
    const toEmail = searchItem.getIn(["data", "email", "value"]);
    if (toEmail && !emailData.to.includes(toEmail)) {
      handleChangeField("to", [...emailData.to, toEmail]);
    }
  };

  const handleApplySnippet = (snippet: any) => {
    if (snippet.subject) handleChangeField("subject", snippet.subject);
    if (snippet.description && editorInputDescriptionRef?.current) {
      editorInputDescriptionRef.current.root.innerHTML = snippet.description;
      handleChangeField("body", snippet.description);
    }
  };

  return (
    <div className="w-3xl space-y-4">
      <div>
        <div className="w-full h-8 flex items-center">
          <label className="flex-none w-20 text-sm font-semibold text-gray-400">{t("From")}</label>
          <div className="w-full flex items-center gap-2 px-1">
            <img
              src={currentUser.get("avatarUrl")}
              alt={currentUser.get("displayName")}
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-sm">{currentUser.get("displayName") || currentUserEmail}</span>
            {currentUser.get("displayName") ? (
              <span className="text-xs text-gray-600">{currentUserEmail}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div>
        <div className="w-full h-8 flex items-center">
          <label className="flex-none w-20 text-sm font-semibold text-gray-400">{t("To")}</label>
          <div className="w-full h-full">
            <RecipientsSelect
              id="to"
              projectId={projectId}
              value={emailData.to}
              onChange={(value) => {
                handleChangeField("to", value);
              }}
              placeholder={t("Select recipients...")}
              isDisabled={!isEditMode}
            />
          </div>
          {!showCcBcc && (
            <div
              className="px-2 w-fit min-w-0 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer"
              onClick={() => setShowCcBcc(true)}
            >
              {t("Cc / Bcc")}
            </div>
          )}
        </div>
        {validationErrors.to && <p className="text-xs text-red-600 mt-1">{validationErrors.to}</p>}
      </div>
      {showCcBcc && (
        <>
          <div>
            <div className="w-full h-8 flex items-center">
              <label className="flex-none w-20 text-sm font-semibold text-gray-400">
                {t("Cc")}
              </label>
              <div className="w-full h-full">
                <RecipientsSelect
                  id="cc"
                  projectId={projectId}
                  value={emailData.cc}
                  onChange={(value) => {
                    handleChangeField("cc", value);
                  }}
                  placeholder={t("Select...")}
                  isDisabled={!isEditMode}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="w-full h-8 flex items-center">
              <label className="flex-none w-20 text-sm font-semibold text-gray-400">
                {t("Bcc")}
              </label>
              <div className="w-full h-full">
                <RecipientsSelect
                  id="bcc"
                  projectId={projectId}
                  value={emailData.bcc}
                  onChange={(value) => {
                    handleChangeField("bcc", value);
                  }}
                  placeholder={t("Select...")}
                  isDisabled={!isEditMode}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <div>
        <div className="w-full h-8 flex items-center">
          <label className="flex-none w-20 text-sm font-semibold text-gray-400">
            {t("Subject")}
          </label>
          <InputDebounce
            placeholder={t("Enter subject...")}
            value={emailData.subject}
            onChange={(value) => handleChangeField("subject", value)}
            disabled={!isEditMode}
            className="outline-none text-sm placeholder:text-sm placeholder:text-gray-400"
          />
        </div>
        {validationErrors.subject && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.subject}</p>
        )}
      </div>
      <div>
        <div className="w-full max-w-full rounded-lg border border-gray-300 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!padding-0 [&_.ql-container]:!border-0 [&_.ql-editor]:min-h-[220px] [&_.ql-toolbar_button:not([class*='ql-'])]:w-auto!">
          <div
            ref={toolbarRef}
            className={`flex items-center flex-wrap gap-1 px-2 py-1.5 border-b border-gray-200 ${!isEditMode ? "hidden" : ""}`}
          >
            {/* Item picker in toolbar */}
            <ToolbarItemPicker
              selectedItem={emailData.item}
              onSelectItem={(searchItem) => {
                if (searchItem.get("id")) {
                  handleSelectSearchItem(searchItem);
                } else {
                  handleChangeField("item", {
                    type: searchItem.get("type"),
                  });
                }
              }}
              isDisabled={!isEditMode}
            />
            {validationErrors.linkItem && (
              <p className="text-xs text-red-600">{validationErrors.linkItem}</p>
            )}
            <div className="w-px h-4 bg-gray-300 mx-0.5" />
            <EmailSnippetPicker iconOnly onSelect={handleApplySnippet} />
            <EmailVariablePicker
              quillRef={editorInputDescriptionRef}
              openRef={variablePickerOpenRef}
            />
            <div className="w-px h-4 bg-gray-300 mx-0.5" />
            <span className="ql-formats">
              <button className="ql-bold" />
              <button className="ql-italic" />
              <button className="ql-underline" />
            </span>
            <span className="ql-formats">
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
            </span>
            <span className="ql-formats">
              <button className="ql-indent" value="-1" />
              <button className="ql-indent" value="+1" />
              <button className="ql-link" />
            </span>
            <EmailCanvaPicker
              projectId={projectId}
              navigateToUrl={navigateToUrl}
              quillRef={editorInputDescriptionRef}
              openRef={canvaPickerOpenRef}
            />
          </div>
          <Editor
            editorInputRef={editorInputDescriptionRef}
            toolbarContainer={toolbarRef}
            showToolbar={isEditMode}
            placeholder={t("Start typing your email, or @ to mention, {{ to show smart values")}
            minRows={8}
            members={members}
            attributes={attributes}
            editorContent={{
              htmlContent: emailData.body,
              contents: {},
            }}
            onChange={(content) => {
              handleChangeField("body", content.htmlContent);
            }}
            onOpenVariablePicker={() => variablePickerOpenRef.current?.()}
            readOnly={!isEditMode}
          />
        </div>
        {validationErrors.body && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.body}</p>
        )}
      </div>
    </div>
  );
};

export default ComposeEmail;
